/**
 * BOW content map (ADR-073 Amendment 2 A2.2 / A2.8).
 *
 * The BOW bookend modals (bow-welcome, bow-complete) are CONTENT-DRIVEN: their
 * copy lives here, not hardcoded inline in the renderers. This is the first
 * brick of the future Flow Composer (ADR-076) — when admins can re-word modals,
 * this map is the override target. It deliberately avoids the inline-copy debt
 * the pa-* steps created.
 *
 * Shape is intentionally simple (a flat slot map) so a future DDB-backed copy
 * override can replace it without renderer changes.
 */

export interface BowStepPreviewItem {
  num: number;
  label: string;
  description: string;
}

export interface BowWelcomeContent {
  heading: string;
  body: string;
  steps: BowStepPreviewItem[];
  footnote: string;
  cta: string;
}

export interface BowCompleteContent {
  heading: string;
  body: string;
  confirmCta: string;
  /** Shown after a successful provision. {org} is substituted at render time. */
  successHeading: string;
  successBody: string;
}

export interface BowContent {
  welcome: BowWelcomeContent;
  complete: BowCompleteContent;
}

export const BOW_CONTENT: BowContent = {
  welcome: {
    heading: 'Welcome to the Nexanomics Beta Program',
    body: "You're onboarding a beta participant. Walk through the steps below to capture their domain and business profile — we'll create their account and email them an activation link at the end. No card or plan is required for beta.",
    steps: [
      {
        num: 1,
        label: "Authorize the participant's domain",
        description: 'Enter the website Nexanomics will monitor and confirm their authorisation.',
      },
      {
        num: 2,
        label: 'Capture business profile',
        description: 'Business details plus the Nexa consultant onboarding them.',
      },
      {
        num: 3,
        label: 'Adapt the persona',
        description: 'Audience and funnel model used to personalise their dashboard.',
      },
      {
        num: 4,
        label: 'Create the beta account',
        description: 'We create their account and email an activation link — the snippet follows after they activate.',
      },
    ],
    footnote: 'This takes about 5 minutes. The participant completes activation and snippet install by email afterwards.',
    cta: 'Start beta onboarding →',
  },
  complete: {
    heading: 'Create the beta account',
    body: 'This creates the participant’s account and domain, flags them as a beta participant, and emails them an activation link to set their password. The trust snippet is emailed after they activate.',
    confirmCta: 'Create beta account & send activation',
    successHeading: 'Beta participant onboarded',
    successBody: 'The account has been created and an activation email is on its way. They’ll appear under Beta participants on the dashboard until their first beacon arrives.',
  },
};
